/**
 * Sélecteur d'aliment cherchable (combobox maison, accessible).
 * Champ texte qui filtre la liste au fil de la frappe ; favoris en tête (⭐).
 * Navigation clavier : ↑↓ pour parcourir, Entrée pour choisir, Échap pour fermer.
 * Épingler/dé-épingler un favori directement depuis la liste.
 */
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type Food, searchFoods } from '@/domain';
import { useAddFavorite, useFoodFavorites, useFoods, useRemoveFavorite } from '@/hooks/queries';

interface Props {
  value: string; // id de l'aliment choisi
  onChange: (foodId: string) => void;
  id?: string;
  required?: boolean;
}

export function FoodPicker({ value, onChange, id, required }: Props) {
  const { data: foods = [] } = useFoods();
  const { data: favorites = [] } = useFoodFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const optId = (i: number) => `${listId}-opt-${i}`;
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const favByFood = useMemo(
    () => new Map(favorites.map((f) => [f.foodId, f.favoriteId])),
    [favorites],
  );
  const favoriteIds = useMemo(() => new Set(favByFood.keys()), [favByFood]);
  const options = useMemo(
    () => searchFoods(foods, favoriteIds, query),
    [foods, favoriteIds, query],
  );
  const favPending = addFavorite.isPending || removeFavorite.isPending;

  const selected: Food | undefined = foods.find((f) => f.id === value);
  // Ce qu'affiche le champ : la saisie en cours si ouvert, sinon le nom choisi.
  const inputText = open ? query : (selected?.name ?? '');

  // Garde l'option surlignée visible dans la liste défilante.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('.combobox-option.active')?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  function choose(food: Food) {
    onChange(food.id);
    setQuery('');
    setHighlight(0);
    setOpen(false);
  }

  function toggleFavorite(foodId: string) {
    if (favPending) return; // évite le double-clic → 2e POST rejeté par l'index unique
    const favId = favByFood.get(foodId);
    if (favId) removeFavorite.mutate(favId);
    else addFavorite.mutate(foodId);
  }

  function moveHighlight(delta: number) {
    setHighlight((h) => {
      const next = Math.max(0, Math.min(h + delta, options.length - 1));
      // Défilement immédiat (avant le prochain rendu) vers l'option ciblée.
      document.getElementById(optId(next))?.scrollIntoView({ block: 'nearest' });
      return next;
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      moveHighlight(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
    } else if (e.key === 'Enter') {
      if (open && options[highlight]) {
        e.preventDefault();
        choose(options[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="combobox">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[highlight] ? optId(highlight) : undefined}
        autoComplete="off"
        // Garde-fou soft : `required` déclenche la validation native tant qu'aucun
        // aliment n'est choisi. La vraie garde est côté appelant (`if (!foodId) return`
        // dans addConsumption/logNow), car un texte libre non résolu satisfait `required`.
        required={required && !value}
        placeholder="Rechercher un aliment…"
        value={inputText}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => {
          setQuery('');
          setHighlight(0);
          setOpen(true);
        }}
        onBlur={() => {
          // Délai : laisser un clic sur une option (ou l'étoile) se déclencher.
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: motif ARIA combobox/listbox standard, clavier géré sur l'input role=combobox
        <ul className="combobox-list" id={listId} role="listbox" ref={listRef}>
          {options.length === 0 ? (
            <li className="combobox-empty" aria-disabled="true">
              Aucun aliment
            </li>
          ) : (
            options.map((f, i) => (
              <li
                key={f.id}
                id={optId(i)}
                // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: option de listbox ARIA (sélection au clic/Entrée depuis l'input)
                role="option"
                tabIndex={-1}
                aria-selected={i === highlight}
                className={`combobox-option${i === highlight ? ' active' : ''}`}
              >
                {/* biome-ignore lint/a11y/noStaticElementInteractions: le clic est un raccourci souris ; la sélection clavier passe par l'input role=combobox */}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: navigation clavier gérée sur l'input (role=combobox) */}
                <span
                  className="combobox-label"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(f)}
                >
                  {f.isFavorite && <span aria-hidden="true">⭐ </span>}
                  {f.name}{' '}
                  <span className="muted">
                    ({f.kcal100g} kcal/100g){f.isPublic ? '' : ' · perso'}
                  </span>
                </span>
                <button
                  type="button"
                  className="combobox-star"
                  aria-label={
                    f.isFavorite ? `Retirer ${f.name} des favoris` : `Ajouter ${f.name} aux favoris`
                  }
                  aria-pressed={f.isFavorite}
                  disabled={favPending}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (blurTimer.current) clearTimeout(blurTimer.current);
                  }}
                  onClick={() => toggleFavorite(f.id)}
                >
                  {f.isFavorite ? '★' : '☆'}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
